import { Injectable } from '@angular/core';
import { Http, Response } from '@angular/http';
import { Observable } from 'rxjs/Rx';
import { DataProviderService } from './data-provider.service';
import { AttributeDataService } from "./attribute-data.service";
import { IDataSource } from './IDataSource';
import { IModel } from './IModel';
import { Appliance } from './enums';

@Injectable()
export class ModelDataService {
    public static readonly WcFrameModelNo = '8651.1';
    public static readonly UrinalFrameModelNo = '8651.2';
    public static readonly WcIllumFrameModelNo = '8650.1';

    constructor(
        private dataProvider: DataProviderService,
        private attributes: AttributeDataService) { }

    public getModel(key: string, withDetails: boolean = true): Observable<IModel> {
        const dataSource: Observable<IDataSource> = this.dataProvider.getData(withDetails);

        return dataSource.map(ds => {
            const model = ds.models[key];
            if (model === undefined)
                throw new Error(`Model "${key}" does not exist.`);

            return model;
        });
    }

    public getModels(withDetails: boolean = true): Observable<IModel> {
        const dataSource: Observable<IDataSource> = this.dataProvider.getData(withDetails);

        return dataSource
            .map(ds => Object.values(ds.models) as IModel[])
            .switchMap(models => Observable.from(models));
    }

    /**
     * Gets an observable yielding all actuator-plate models.
     */
    public getPlates(withDetails: boolean = true): Observable<IModel> {
        return this.getModelsOfType("plate", withDetails);
    }

    /**
     * Gets an observable yielding all frame models.
     */
    public getFrames(withDetails: boolean = true): Observable<IModel> {
        return this.getModelsOfType("frame", withDetails);
    }

    /**
     * Gets an observable yielding all accessory models.
     */
    public getAccessories(withDetails: boolean = true): Observable<IModel> {
        return this.getModelsOfType("accessory", withDetails);
    }

    /**
     * Gets an observable yielding all prewall models.
     */
    public getPrewalls(withDetails: boolean = true): Observable<IModel> {
        return this.getModelsOfType("prewall", withDetails);
    }

    /**
     * Gets an observable yielding all models which allow a frame to be attached to them.
     */
    public modelsWithBorderFrameSupport(): Observable<string[]> {
        return this.getModelsOfType("frame", true)
            .map(model => model["associated-model-keys"])
            .reduce((modelKeys1, modelKeys2) => modelKeys1.concat(modelKeys2));
    }

    /**
     * Gets the corresponding frame model by the given appliance.
     * @param appliance wc or urinal.
     */
    public getFrameModelByAppliance(appliance: 'wc' | 'urinal'): Observable<IModel> {
        const modelNo = appliance === 'wc' ? ModelDataService.WcFrameModelNo : ModelDataService.UrinalFrameModelNo;

        return this.getModelsOfType("frame", true)
            .filter(m => m["model-number"] === modelNo)
            .do(m => console.error('frame: ' + m["model-number"]));
    }

    /**
     * Gets the corresponding frame model by the given appliance.
     * @param appliance wc or urinal.
     */
    public getIllumFrameModel(): Observable<IModel> {
        return this.getModelsOfType("frame", true)
            .filter(m => m["model-number"] === ModelDataService.WcIllumFrameModelNo)
            .do(m => console.error('frame: ' + m["model-number"]));
    }

    public modelHasFrameSupport(model: IModel): boolean {
        return model.attributes['tile-bounding'] && model.attributes['tile-bounding'][0] !== 0;
    }

    public modelHasIllumFrameSupport(model: IModel): boolean {
        return model.attributes['illum-frame'] && model.attributes['illum-frame'][0] !== 0;
    }

    public frameModelNoByAppliance(appliance: 'wc' | 'urinal'): string {
        return appliance === 'wc' ? ModelDataService.WcFrameModelNo : ModelDataService.UrinalFrameModelNo;
    }

    public illumFrameModelNo(): string {
        return ModelDataService.WcIllumFrameModelNo;
    }

    private getModelsOfType(modelGroupName: string, withDetails: boolean): Observable<IModel> {
        const modelGroups$: Observable<string[]> = this.attributes.getModelGroup();
        const allModels$: Observable<IModel> = this.getModels(withDetails);

        let modelGroupIndex: number;
        return <Observable<IModel>>modelGroups$
            .do((modelGroups: string[]) => modelGroupIndex = modelGroups.indexOf(modelGroupName))
            .concat(allModels$)
            .skip(1)
            .filter((model: IModel) => {
                const attributeDict = model.attributes;
                console.assert(attributeDict !== undefined, `Model "${model.key}" has no "attributes" object.`);

                const modelGroupArray: number[] = attributeDict["model-group"];
                console.assert(modelGroupArray !== undefined, `Model "${model.key}" has no "model-group" attribute.`);
                console.assert(modelGroupArray.length === 1, `Model "${model.key}" has a "model-group" attribute with an array length of != 1.`);

                const modelGroup = modelGroupArray[0];
                return modelGroup === modelGroupIndex;
            });
    }
}



// WEBPACK FOOTER //
// ./src/app/core/model-data.service.ts